'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { AlertCircle, Maximize2, RotateCw, Loader2, Check, X } from 'lucide-react';
import { createLogger } from '@/shared/lib/logger';
import {
  useImageLoader,
  useCropCanvas,
  useCropInteraction,
  CropOverlay,
  AspectRatioSelector,
  type CropArea,
} from './image-crop';
import {
  ASPECT_RATIOS,
  calculateInitialCropRect,
  findClosestAspectRatio,
  invertedAspectRatio,
  type AspectRatioKey,
} from '@/shared/constants/aspect-ratios';

const logger = createLogger('UnifiedImageCropModal');

interface UnifiedImageCropModalProps {
  imageUrl: string;
  aspectRatio?: number;
  isOpen?: boolean;
  onSave: (croppedBlob: Blob) => void | Promise<void>;
  onCancel: () => void;
  onSkipCrop?: () => void | Promise<void>;
  title?: string;
  saveButtonText?: string;
  minWidth?: number;
  minHeight?: number;
  showSkipButton?: boolean;
  currentIndex?: number;
  totalImages?: number;
  /** Show aspect ratio selector UI */
  showAspectRatioSelector?: boolean;
  /** Initial aspect ratio key (auto-detected if not provided) */
  initialAspectRatio?: AspectRatioKey;
  /** Max pixels on longest side (e.g. 2048 for angles, 768 for references) */
  maxLongSide?: number;
  /** JPEG output size budget in bytes. Defaults to 900KB. */
  maxFileSize?: number;
}

export function UnifiedImageCropModal({
  imageUrl,
  aspectRatio: propAspectRatio,
  isOpen = true,
  onSave,
  onCancel,
  onSkipCrop,
  title,
  saveButtonText,
  minWidth = 100,
  minHeight = 100,
  showSkipButton = false,
  currentIndex,
  totalImages,
  showAspectRatioSelector = false,
  initialAspectRatio,
  maxLongSide,
  maxFileSize,
}: UnifiedImageCropModalProps) {
  const t = useTranslations('imageCrop');
  const tCommon = useTranslations('common');
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // User-driven 90° rotation produces a new blob URL we feed back into the
  // loader. Keeping a separate state lets us revoke the previous object URL.
  const [rotatedImageUrl, setRotatedImageUrl] = useState<string | null>(null);
  const effectiveImageUrl = rotatedImageUrl ?? imageUrl;

  // Image loading
  const { imageLoaded, imageError, imageNaturalSize } = useImageLoader({
    imageUrl: effectiveImageUrl,
  });

  // Revoke any locally created blob URL when it's replaced or on unmount.
  // The cleanup runs before the next effect (i.e. when rotatedImageUrl changes)
  // and at unmount, so all created URLs are released exactly once.
  useEffect(() => {
    return () => {
      if (rotatedImageUrl) URL.revokeObjectURL(rotatedImageUrl);
    };
  }, [rotatedImageUrl]);
  // Drop any stale rotation when the upstream source URL changes.
  useEffect(() => {
    setRotatedImageUrl(null);
  }, [imageUrl]);

  // Aspect ratio state (for selector mode)
  const [selectedAspectRatioKey, setSelectedAspectRatioKey] =
    useState<AspectRatioKey | null>(initialAspectRatio ?? null);

  // Compute actual aspect ratio value
  const aspectRatio = showAspectRatioSelector
    ? selectedAspectRatioKey
      ? ASPECT_RATIOS[selectedAspectRatioKey].ratio
      : undefined
    : propAspectRatio;

  // Crop area state
  const [cropArea, setCropArea] = useState<CropArea>({
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });

  // Canvas/save operations
  const { canvasRef, isSaving, handleSave } = useCropCanvas({
    onSave,
    maxLongSide,
    maxFileSize,
  });
  const [isSkipping, setIsSkipping] = useState(false);

  // Crop interaction (drag/resize)
  const { handleMouseDown, handleTouchStart } = useCropInteraction({
    imageRef,
    imageNaturalSize,
    aspectRatio,
    minWidth,
    minHeight,
    onCropAreaChange: setCropArea,
    cropArea,
  });

  // Auto-detect aspect ratio on image load (only if selector is shown and no initial ratio)
  useEffect(() => {
    if (
      showAspectRatioSelector &&
      !initialAspectRatio &&
      imageLoaded &&
      imageNaturalSize.width &&
      imageNaturalSize.height
    ) {
      const detectedRatio = findClosestAspectRatio(
        imageNaturalSize.width,
        imageNaturalSize.height
      );
      setSelectedAspectRatioKey(detectedRatio);
    }
  }, [
    showAspectRatioSelector,
    initialAspectRatio,
    imageLoaded,
    imageNaturalSize.width,
    imageNaturalSize.height,
  ]);

  // Set initial crop area after image is loaded
  useEffect(() => {
    if (!imageLoaded || !imageNaturalSize.width || !imageNaturalSize.height) {
      return;
    }

    if (aspectRatio) {
      // Use helper function to calculate optimal crop rect
      const rect = calculateInitialCropRect(
        imageNaturalSize.width,
        imageNaturalSize.height,
        aspectRatio
      );
      setCropArea(rect);
    } else {
      // Free crop - use full image
      setCropArea({
        x: 0,
        y: 0,
        width: imageNaturalSize.width,
        height: imageNaturalSize.height,
      });
    }
  }, [
    imageLoaded,
    imageNaturalSize.width,
    imageNaturalSize.height,
    aspectRatio,
  ]);

  // Handle aspect ratio change from selector
  const handleAspectRatioChange = useCallback(
    (ratioKey: AspectRatioKey | null) => {
      setSelectedAspectRatioKey(ratioKey);

      // Immediately recalculate crop area with new ratio
      if (!imageNaturalSize.width || !imageNaturalSize.height) return;

      if (ratioKey) {
        const newRatio = ASPECT_RATIOS[ratioKey].ratio;
        const rect = calculateInitialCropRect(
          imageNaturalSize.width,
          imageNaturalSize.height,
          newRatio
        );
        setCropArea(rect);
      } else {
        // Free crop - expand to full image
        setCropArea({
          x: 0,
          y: 0,
          width: imageNaturalSize.width,
          height: imageNaturalSize.height,
        });
      }
    },
    [imageNaturalSize.width, imageNaturalSize.height]
  );

  const handleSaveClick = useCallback(() => {
    handleSave(imageRef.current, cropArea);
  }, [handleSave, cropArea]);

  const handleSkipCrop = useCallback(async () => {
    if (onSkipCrop) {
      setIsSkipping(true);
      try {
        await onSkipCrop();
      } finally {
        setIsSkipping(false);
      }
    }
  }, [onSkipCrop]);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleSaveClick();
    },
    [handleSaveClick]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      logger.debug('Key pressed:', e.key);
      if (e.key === 'Enter') {
        logger.debug('Enter pressed - submitting crop');
        e.preventDefault();
        e.stopPropagation();
        handleSaveClick();
      }
    },
    [handleSaveClick]
  );

  const handleFitToWindow = useCallback(() => {
    if (!imageNaturalSize.width || !imageNaturalSize.height) return;
    setCropArea({
      x: 0,
      y: 0,
      width: imageNaturalSize.width,
      height: imageNaturalSize.height,
    });
  }, [imageNaturalSize]);

  const handleRotate90CW = useCallback(() => {
    const img = imageRef.current;
    if (!img || !img.complete || !img.naturalWidth || !img.naturalHeight) {
      return;
    }
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = h;
    canvas.height = w;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.translate(h / 2, w / 2);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(img, -w / 2, -h / 2);
    canvas.toBlob((blob) => {
      if (!blob) {
        logger.warn('Rotation toBlob returned null (canvas likely tainted)');
        return;
      }
      const url = URL.createObjectURL(blob);
      // The [rotatedImageUrl] cleanup effect revokes the previous URL on swap.
      setRotatedImageUrl(url);
      if (selectedAspectRatioKey) {
        setSelectedAspectRatioKey(invertedAspectRatio(selectedAspectRatioKey));
      }
    }, 'image/jpeg', 0.92);
  }, [selectedAspectRatioKey]);

  const isBusy = isSaving || isSkipping;

  // Error state
  if (imageError) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {tCommon('error')}
            </DialogTitle>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{imageError}</AlertDescription>
          </Alert>
          <div className="flex justify-end">
            <Button onClick={onCancel}>{tCommon('close')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Loading state
  if (!imageLoaded) {
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="sr-only">
              {tCommon('loadingImage')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-4">
            <div className="flex items-center mb-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
              <span>{tCommon('loading')}</span>
            </div>
            {showSkipButton && (
              <Button
                variant="outline"
                onClick={handleSkipCrop}
                disabled={isBusy}
              >
                {t('skipButton')}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        logger.debug('Dialog onOpenChange fired, open:', open);
        if (!open) {
          logger.debug('Dialog close attempt detected');
          return;
        }
      }}
    >
      <DialogContent
        className="w-[95vw] max-w-6xl max-h-[98vh] md:max-h-[95vh] flex flex-col overflow-hidden p-3 md:p-6"
        showCloseButton={false}
        onKeyDown={handleKeyDown}
        onPointerDownOutside={(e) => {
          logger.debug('Pointer down outside dialog - preventing close');
          e.preventDefault();
        }}
        onInteractOutside={(e) => {
          logger.debug('Interact outside dialog - preventing close');
          e.preventDefault();
        }}
      >
        {/* iOS-style header: cancel (X) top-left, confirm (✓) top-right. */}
        <DialogHeader className="flex-row items-start gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onCancel}
            disabled={isBusy}
            aria-label={tCommon('cancel')}
            className="shrink-0 -ml-1"
          >
            <X className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <DialogTitle>
              {title || t('title')}
              {currentIndex !== undefined &&
                totalImages !== undefined &&
                totalImages > 1 && (
                  <span className="ml-2 text-form-help text-muted-foreground">
                    (
                    {t('imageCounter', {
                      current: currentIndex + 1,
                      total: totalImages,
                    })}
                    )
                  </span>
                )}
            </DialogTitle>
            <p className="text-form-help text-muted-foreground mt-1 hidden md:block">
              {t('description')}
            </p>
          </div>
          <Button
            type="button"
            size="icon"
            onClick={() => handleSaveClick()}
            disabled={isBusy}
            aria-label={saveButtonText || t('saveButton')}
            className="shrink-0"
          >
            {isBusy ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Check className="w-5 h-5" />
            )}
          </Button>
        </DialogHeader>

        {/* Aspect Ratio Selector */}
        {showAspectRatioSelector && (
          <div className="py-2 border-b">
            <AspectRatioSelector
              selectedRatio={selectedAspectRatioKey}
              onRatioSelect={handleAspectRatioChange}
            />
          </div>
        )}

        <div className="flex-1 flex items-center justify-center min-h-0 overflow-auto touch-none p-4">
          <div
            ref={containerRef}
            className="relative bg-muted rounded-lg p-2 inline-flex items-center justify-center touch-none"
            style={{ touchAction: 'none' }}
          >
            <div className="relative">
              <img
                ref={imageRef}
                src={effectiveImageUrl}
                alt="Edit preview"
                className="block w-auto h-auto"
                style={{
                  maxWidth: 'min(85vw, 65rem)',
                  maxHeight: 'calc(100vh - 18rem)',
                }}
                draggable={false}
              />

              {imageRef.current &&
                imageNaturalSize.width > 0 &&
                imageNaturalSize.height > 0 && (
                  <CropOverlay
                    cropArea={cropArea}
                    imageNaturalSize={imageNaturalSize}
                    imageRect={imageRef.current.getBoundingClientRect()}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                  />
                )}
            </div>
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />

        <form onSubmit={handleFormSubmit} className="mt-4 flex flex-col gap-3">
          <div className="flex items-center gap-4 text-form-help text-muted-foreground justify-center">
            <span>
              {t('cropSize', {
                width: Math.round(cropArea.width),
                height: Math.round(cropArea.height),
              })}
            </span>
            {showAspectRatioSelector && selectedAspectRatioKey && (
              <span>
                {t('aspectLabel', {
                  label: ASPECT_RATIOS[selectedAspectRatioKey].label,
                })}
              </span>
            )}
            {!showAspectRatioSelector && aspectRatio && (
              <span>{t('aspectValue', { value: aspectRatio.toFixed(2) })}</span>
            )}
          </div>

          {/* Cancel (X) and confirm (✓) live in the header — see DialogHeader.
              The footer keeps the crop tools only. */}
          <div className="flex flex-wrap gap-2 justify-between items-center">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleFitToWindow}
                className="flex items-center gap-2"
                title={t('fit')}
              >
                <Maximize2 className="w-4 h-4" />
                <span className="hidden md:inline">{t('fit')}</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleRotate90CW}
                disabled={isBusy || !imageLoaded}
                className="flex items-center gap-2"
                title={t('rotate')}
              >
                <RotateCw className="w-4 h-4" />
                <span className="hidden md:inline">{t('rotate')}</span>
              </Button>
            </div>

            {showSkipButton && onSkipCrop && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSkipCrop}
                disabled={isBusy}
              >
                {t('skipButton')}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
